/**
 * CommissionDashboardPage -- Dashboard de comisiones por vendedor.
 * KPIs: Total Ventas, Total Comisiones, Pedidos Facturados, Monto Cobrado.
 * Tabla resumen por vendedor con desglose.
 */
import { todayISO } from '../../../lib/utils'
import { formatMoneyForTenant } from '@/lib/locale'
import { DataTable } from '@/components/ds/DataTable'
import { useState } from 'react'
import { DollarSign, TrendingUp, FileCheck, Banknote, Users } from 'lucide-react'
import { useCommissionDashboard, type CommissionSellerSummary } from '../hooks/useSales'
import { usePermission } from '@/hooks/usePermission'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { inputCls as baseInputCls } from '@/lib/formStyles'
import { EmptyState } from '@/components/EmptyState'

const _today = todayISO()
const _firstOfMonth = _today.substring(0, 7) + '-01'

const inputCls = baseInputCls.replace('w-full ', '')

const formatCLP = (val: string | number) =>
  formatMoneyForTenant(typeof val === 'string' ? parseFloat(val) || 0 : val)

interface KpiCardProps {
  icon: React.ElementType
  label: string
  value: string
  iconBgCls?: string
  iconCls?: string
}

function KpiCard({ icon: Icon, label, value, iconBgCls = 'bg-primary-50', iconCls = 'text-primary' }: KpiCardProps) {
  return (
    <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBgCls} flex items-center justify-center`}>
          <Icon size={20} className={iconCls} />
        </div>
        <div>
          <p className="text-sm text-content-muted">{label}</p>
          <p className="text-xl font-bold text-content">{value}</p>
        </div>
      </div>
    </div>
  )
}

export function CommissionDashboardPage() {
  const [dateFrom, setDateFrom] = useState(_firstOfMonth)
  const [dateTo, setDateTo] = useState(_today)
  const [sellerFilter, setSellerFilter] = useState<string>('')

  const { isViewOwn } = usePermission()
  const isSellerOnly = isViewOwn('sales.commissions')

  const { data, isLoading } = useCommissionDashboard({
    date_from: dateFrom,
    date_to: dateTo,
    seller_id: isSellerOnly ? undefined : sellerFilter || undefined,
  })

  const sellers: CommissionSellerSummary[] = data?.sellers ?? []
  const totals = data?.totals

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Comisiones"
        subtitle="Resumen de comisiones por vendedor en el periodo seleccionado"
        help={HELP['sales.commissions']}
      />

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-2">
          <label htmlFor="commissiondashboardpage-desde" className="text-xs text-content-muted font-medium">Desde</label>
          <input id="commissiondashboardpage-desde"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="commissiondashboardpage-hasta" className="text-xs text-content-muted font-medium">Hasta</label>
          <input id="commissiondashboardpage-hasta"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={inputCls}
          />
        </div>

        {!isSellerOnly && (
          <div className="flex items-center gap-2">
            <label htmlFor="commissiondashboardpage-vendedor" className="text-xs text-content-muted font-medium">Vendedor</label>
            <select id="commissiondashboardpage-vendedor"
              value={sellerFilter}
              onChange={(e) => setSellerFilter(e.target.value)}
              className={inputCls + ' min-w-[180px]'}
            >
              <option value="">Todos</option>
              {sellers.map((s) => (
                <option key={s.seller_id} value={s.seller_id}>
                  {s.seller_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {(dateFrom !== _firstOfMonth || dateTo !== _today) && (
          <button
            onClick={() => {
              setDateFrom(_firstOfMonth)
              setDateTo(_today)
            }}
            className="text-xs text-content-subtle hover:text-content-muted transition-colors"
          >
            Restablecer mes actual
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-4 animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-surface-muted rounded-xl" />
            ))}
          </div>
          <div className="h-8 bg-surface-muted rounded" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-surface-muted rounded" />
          ))}
        </div>
      )}

      {!isLoading && data && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              icon={DollarSign}
              label="Total Ventas"
              value={formatCLP(totals?.total_sales ?? '0')}
              iconBgCls="bg-primary-50"
              iconCls="text-primary"
            />
            <KpiCard
              icon={TrendingUp}
              label="Total Comisiones"
              value={formatCLP(totals?.total_commissions ?? '0')}
              iconBgCls="bg-info-surface"
              iconCls="text-info-content-soft"
            />
            <KpiCard
              icon={FileCheck}
              label="Pedidos Facturados"
              value={String(sellers.reduce((acc, s) => acc + s.invoiced_count, 0))}
              iconBgCls="bg-success-surface"
              iconCls="text-success-content-soft"
            />
            <KpiCard
              icon={Banknote}
              label="Monto Cobrado"
              value={formatCLP(sellers.reduce((acc, s) => acc + (parseFloat(s.paid_amount) || 0), 0))}
              iconBgCls="bg-success-surface"
              iconCls="text-success-content-soft"
            />
          </div>

          {/* Sellers Table */}
          {sellers.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No hay datos de comisiones para el periodo seleccionado"
              size="md"
            />
          ) : (
            <DataTable<typeof sellers[number]>
              columns={[
                // La columna del vendedor desaparece cuando quien mira es el
                // propio vendedor: ver su nombre repetido en cada fila no informa.
                ...(!isSellerOnly ? [{
                  key: 'seller_name', header: 'Vendedor', sortable: true,
                  render: (s: typeof sellers[number]) => (
                    <span className="text-content font-medium">{s.seller_name}</span>
                  ),
                }] : []),
                { key: 'orders_count', header: 'N. Pedidos', numeric: true, sortable: true,
                  sortValue: s => Number(s.orders_count) },
                { key: 'invoiced_count', header: 'Facturados', numeric: true, sortable: true,
                  sortValue: s => Number(s.invoiced_count) },
                { key: 'total_sales', header: 'Venta Neta', numeric: true, sortable: true,
                  sortValue: s => Number(s.total_sales),
                  render: s => <span className="text-content">{formatCLP(s.total_sales)}</span> },
                { key: 'total_commissions', header: 'Comision', numeric: true, sortable: true,
                  sortValue: s => Number(s.total_commissions),
                  render: s => (
                    <span className="font-semibold text-info-content">{formatCLP(s.total_commissions)}</span>
                  ) },
                { key: 'paid_amount', numeric: true, sortable: true,
                  header: <span className="text-success-content-soft">Cobrado</span>,
                  sortValue: s => Number(s.paid_amount),
                  render: s => (
                    <span className="font-semibold text-success-content-soft">
                      {formatCLP(s.paid_amount)}
                    </span>
                  ) },
                { key: 'pending_amount', numeric: true, sortable: true,
                  header: <span className="text-warning-content-soft">Pendiente</span>,
                  sortValue: s => Number(s.pending_amount),
                  render: s => (
                    <span className="font-semibold text-warning-content-soft">
                      {formatCLP(s.pending_amount)}
                    </span>
                  ) },
              ]}
              rows={sellers}
              rowKey={s => s.seller_id}
              minWidth={900}
              // El pie solo tiene sentido con más de un vendedor: con uno solo
              // repetiría su propia fila.
              footer={sellers.length > 1 ? {
                orders_count: sellers.reduce((acc, s) => acc + s.orders_count, 0),
                invoiced_count: sellers.reduce((acc, s) => acc + s.invoiced_count, 0),
                total_sales: formatCLP(sellers.reduce((acc, s) => acc + (parseFloat(String(s.total_sales)) || 0), 0)),
                total_commissions: (
                  <span className="text-info-content">
                    {formatCLP(sellers.reduce((acc, s) => acc + (parseFloat(String(s.total_commissions)) || 0), 0))}
                  </span>
                ),
                paid_amount: (
                  <span className="text-success-content-soft">
                    {formatCLP(sellers.reduce((acc, s) => acc + (parseFloat(String(s.paid_amount)) || 0), 0))}
                  </span>
                ),
                pending_amount: (
                  <span className="text-warning-content-soft">
                    {formatCLP(sellers.reduce((acc, s) => acc + (parseFloat(String(s.pending_amount)) || 0), 0))}
                  </span>
                ),
              } : undefined}
              caption="Comisiones por vendedor del período"
            />
          )}
        </>
      )}
    </div>
  )
}
